import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { fetchValidatedToken } from '../session'
import store from '../store'
import { clearSession } from '../session/actions'
import { User } from './types'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

type StreamOptions = {
  onChunk?: (chunk: string) => void
  onComplete?: (fullResponse: string) => void
  stream?: boolean
}

type ProfileGenerationResponse = {
  success: boolean
  introduction: string
  stream?: ReadableStream<string>
}

/**
 * Hook for generating profile content with support for streaming responses.
 *
 * @returns A mutation function that can be called in two ways:
 *   - With a User object: mutation.mutate(user)
 *   - With options for streaming: mutation.mutate({ data: user, options: { stream: true, onChunk: callback } })
 *
 * The response object contains:
 *   - success: A boolean indicating if the generation was successful
 *   - introduction: The generated introduction text
 *   - stream: A ReadableStream of the text being generated (when streaming is enabled)
 *
 * @example
 * // Basic usage
 * const mutation = useGenerateProfileMutation();
 * mutation.mutate(user);
 *
 * @example
 * // With streaming using callbacks
 * const mutation = useGenerateProfileMutation();
 * mutation.mutate({
 *   data: user,
 *   options: {
 *     stream: true,
 *     onChunk: (chunk) => setCurrentText(prev => prev + chunk),
 *     onComplete: (text) => console.log('Generation complete:', text)
 *   }
 * });
 *
 * @example
 * // With stream consumption in component
 * const mutation = useGenerateProfileMutation();
 * const { stream } = await mutation.mutateAsync({
 *   data: user,
 *   options: { stream: true }
 * });
 *
 * if (stream) {
 *   const reader = stream.getReader();
 *   while (true) {
 *     const { done, value } = await reader.read();
 *     if (done) break;
 *     setCurrentText(prev => prev + value);
 *   }
 * }
 */
export const useGenerateProfileMutation = () => {
  return useMutation<
    ProfileGenerationResponse,
    Error,
    User | { data: User; options?: StreamOptions }
  >({
    mutationFn: async (
      params: User | { data: User; options?: StreamOptions }
    ): Promise<ProfileGenerationResponse> => {
      // Handle both direct User object and params object with options
      const data = 'userId' in params ? params : (params as any).data
      const options = 'userId' in params ? undefined : (params as any).options
      const userId = data?.userId ?? data.id

      // Check if userId exists
      if (!data || !userId) {
        throw new Error('userId is required for profile generation')
      }
      // Retrieve/refresh the token asynchronously
      const token = await fetchValidatedToken(URL)(store)

      // If no token is available, there's no point in making the request
      if (!token) {
        console.log('No valid token available for authenticated request')
        store.dispatch(clearSession({ invalidSession: true }))
        throw new Error('Authentication required')
      }

      try {
        const response = await fetch(`${URL}/profile/${userId}/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: userId,
            stream:
              options?.stream !== undefined
                ? options.stream
                : Boolean(options?.onChunk),
          }),
        })

        if (response.status === 401) {
          console.log('Received 401 Unauthorized from API')
          store.dispatch(clearSession({ invalidSession: true }))
          throw new Error('Session expired')
        }

        if (!response.ok) {
          throw new Error(
            `Network response was not ok: ${response.status} ${response.statusText}`
          )
        }

        // Determine if we should handle streaming based on response body and options
        const shouldHandleStream =
          response.body &&
          (options?.stream ||
            (options?.stream === undefined && options?.onChunk))

        console.log(
          `Profile generation request for userId: ${userId}, streaming: ${shouldHandleStream}`
        )

        if (shouldHandleStream) {
          // Create a ReadableStream to be returned
          let chunks: string[] = []
          let textContents = ''

          // Create a new ReadableStream from the response body
          const stream = new ReadableStream<string>({
            async start(controller) {
              // Get the reader from the response body
              if (!response.body) {
                throw new Error('Response body is null')
              }
              const reader = response.body.getReader()
              const decoder = new TextDecoder()

              try {
                while (true) {
                  const { done, value } = await reader.read()

                  if (done) {
                    controller.close()
                    break
                  }

                  const chunk = decoder.decode(value, { stream: true })
                  chunks.push(chunk)
                  textContents += chunk

                  // Log each chunk to verify streaming is working
                  console.log(`Received stream chunk: "${chunk}"`)

                  // Process chunk to handle quotes and escaping
                  let processedChunk = chunk

                  // For the first chunk, remove starting quote if present
                  if (chunks.length === 1 && processedChunk.startsWith('"')) {
                    processedChunk = processedChunk.substring(1)
                  }

                  // For the last chunk (since we don't know if it's last), remove ending quote
                  if (processedChunk.endsWith('"')) {
                    const lastQuoteIdx = processedChunk.lastIndexOf('"')
                    if (
                      lastQuoteIdx === processedChunk.length - 1 &&
                      (lastQuoteIdx === 0 ||
                        processedChunk[lastQuoteIdx - 1] !== '\\')
                    ) {
                      processedChunk = processedChunk.substring(0, lastQuoteIdx)
                    }
                  }

                  // Replace escaped characters
                  processedChunk = processedChunk
                    .replace(/\\"/g, '"')
                    .replace(/\\n/g, '\n')
                    .replace(/\\t/g, '\t')
                    .replace(/\\\\/g, '\\')

                  // Enqueue the processed chunk to the stream
                  if (processedChunk) {
                    controller.enqueue(processedChunk)
                  }

                  // Call the onChunk callback with each new chunk if provided
                  if (options?.onChunk && processedChunk) {
                    options.onChunk(processedChunk)
                  }
                }
              } catch (error) {
                console.error('Stream reading error:', error)
                controller.error(error)
              }
            },
          })

          // Call the onComplete callback with the full response
          if (options?.onComplete) {
            options.onComplete(textContents)
          }

          console.log(
            `Stream complete. Total length: ${textContents.length} characters`
          )

          // Clean up any JSON-style string escaping
          let cleanedResponse = textContents

          // Remove surrounding quotes if present
          if (
            cleanedResponse.startsWith('"') &&
            cleanedResponse.endsWith('"')
          ) {
            cleanedResponse = cleanedResponse.substring(
              1,
              cleanedResponse.length - 1
            )
          }

          // Replace escaped quotes
          cleanedResponse = cleanedResponse.replace(/\\"/g, '"')

          // Replace other common escaped characters
          cleanedResponse = cleanedResponse
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
            .replace(/\\\\/g, '\\')

          console.log(
            `Cleaned response: "${cleanedResponse.substring(0, 50)}..."`
          )

          // Return the cleaned text as the introduction, along with the stream
          return {
            success: true,
            introduction: cleanedResponse,
            stream,
          }
        } else {
          // Fall back to regular response if streaming not supported/requested
          console.log('Using standard response (non-streaming)')

          // First get the response as text so we can try both approaches
          const responseText = await response.text()

          try {
            // Try to parse as JSON
            const jsonResponse = JSON.parse(responseText)
            if (jsonResponse && jsonResponse.introduction !== undefined) {
              console.log(
                `Standard JSON response received: success=${
                  jsonResponse.success
                }, introduction length: ${
                  jsonResponse.introduction?.length || 0
                }`
              )
              return jsonResponse as { success: boolean; introduction: string }
            } else {
              throw new Error('Invalid JSON structure')
            }
          } catch (error) {
            // If JSON parsing fails, assume it's plain text
            console.log(
              `Standard text response received: length: ${
                responseText.length || 0
              }`
            )

            // Clean up any JSON-style string escaping
            let cleanedResponse = responseText

            // Remove surrounding quotes if present
            if (
              cleanedResponse.startsWith('"') &&
              cleanedResponse.endsWith('"')
            ) {
              cleanedResponse = cleanedResponse.substring(
                1,
                cleanedResponse.length - 1
              )
            }

            // Replace escaped quotes
            cleanedResponse = cleanedResponse.replace(/\\"/g, '"')

            // Replace other common escaped characters
            cleanedResponse = cleanedResponse
              .replace(/\\n/g, '\n')
              .replace(/\\t/g, '\t')
              .replace(/\\\\/g, '\\')

            console.log(
              `Cleaned response: "${cleanedResponse.substring(0, 50)}..."`
            )

            return {
              success: true,
              introduction: cleanedResponse,
            }
          }
        }
      } catch (error) {
        throw error
      }
    },
    onError: (error) => {
      console.error('Profile generation error:', error)
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      })
      toast.error(`Failed to generate profile: ${error.message}`)
    },
  })
}
